import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Chip,
  Button,
  TextField,
  Stack,
} from "@mui/material";
import { NotificationsActive, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const API_URL = "https://api.hr-ebnhaian.cloud/api/v1/doctors/expiring-documents";

/* =========================
   Helpers
========================= */
const daysLeft = (date) => {
  const today = new Date();
  const expiry = new Date(date);
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
};

const getColor = (days) => {
  if (days <= 30) return "error";
  if (days <= 60) return "warning";
  return "default";
};

const getStatusLabel = (days) => {
  if (days < 0) return "Expired";
  if (days <= 30) return "Critical";
  return "Warning";
};

/* =========================
   Component
========================= */
function DoctorAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  /* =========================
     Fetch Alerts
  ========================= */
  useEffect(() => {
    axios
      .get(API_URL, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((res) => {
        setAlerts(res.data.data || []);
      });
  }, []);

  /* =========================
     Filter by Search
  ========================= */
  const filteredAlerts = alerts.filter((row) => {
    const q = search.toLowerCase();

    return (
      row.name?.toLowerCase().includes(q) ||
      row.doctorCode?.toLowerCase().includes(q) ||
      row.type?.toLowerCase().includes(q) ||
      row.center?.toLowerCase().includes(q)
    );
  });

  return (
    <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          🚨 Doctors Expiring Documents
        </Typography>
      </Stack>

      {/* SEARCH */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by doctor name, code or document..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <Search sx={{ mr: 1 }} />,
        }}
        sx={{ mb: 3 }}
      />

      {/* TABLE */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#d32f2f" }}>
            <TableRow>
              <TableCell sx={{ color: "white" }}>Doctor</TableCell>
              <TableCell sx={{ color: "white" }}>Center</TableCell>
              <TableCell sx={{ color: "white" }}>Document</TableCell>
              <TableCell sx={{ color: "white" }}>Status</TableCell>
              <TableCell sx={{ color: "white" }}>Days Left</TableCell>
              <TableCell sx={{ color: "white" }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredAlerts.map((row, i) => {
              const days = daysLeft(row.expiryDate);
              const color = getColor(days);

              return (
                <TableRow
                  key={i}
                  sx={days <= 30 ? { animation: "blink 1s infinite" } : {}}
                >
                  <TableCell>
                    <Typography fontWeight="bold">
                      {row.name}
                    </Typography>
                    <Typography variant="caption">
                      {row.doctorCode}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {row.center || "-"}
                  </TableCell>

                  <TableCell>
                    <Chip
                      icon={<NotificationsActive />}
                      label={
                        row.type === "MEDICAL_LICENSE"
                          ? `${row.type} (${row.authority})`
                          : row.type
                      }
                      color={color}
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={getStatusLabel(days)}
                      color={color}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography color={color}>
                      {days < 0 ? "Expired" : `${days} days`}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      color={color}
                      onClick={() =>
                        navigate(`/doctors/${row.doctorId}`)
                      }
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredAlerts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  🎉 No expiring documents found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* BLINK EFFECT */}
      <style>
        {`
          @keyframes blink {
            0% { background-color: #ffebee; }
            50% { background-color: #ffffff; }
            100% { background-color: #ffebee; }
          }
        `}
      </style>
    </Box>
  );
}

export default DoctorAlerts;
